import { tarslogs, TarslogsVue, setupAutoCapture } from '@tarslogs/client'
import type { App } from 'vue'

const TARSLOGS_URL = import.meta.env.VITE_TARSLOGS_URL
const TARSLOGS_PROJECT_ID = import.meta.env.VITE_TARSLOGS_PROJECT_ID
const TARSLOGS_API_KEY = import.meta.env.VITE_TARSLOGS_API_KEY

export const tarslogsEnabled = !!(TARSLOGS_URL && TARSLOGS_PROJECT_ID && TARSLOGS_API_KEY)

export function installTarslogs(app: App): void {
  if (!tarslogsEnabled) return

  tarslogs.init({
    url: TARSLOGS_URL!,
    projectId: TARSLOGS_PROJECT_ID!,
    apiKey: TARSLOGS_API_KEY!,
    environment: import.meta.env.MODE,
    version: __COMMIT_HASH__,
    rateLimitPerMinute: 30,
    debug: import.meta.env.DEV,
  })

  // Saved-meeting capabilities live in the URL fragment. The browser does not
  // send fragments over HTTP, but the TarsLogs client reads location.href for
  // browser errors. Sanitize the queued payload without touching browser
  // history, which avoids replaceState throttling during an error storm.
  type ErrorPayload = { context?: Record<string, unknown>; [key: string]: unknown }
  type TarslogsRuntime = { transport?: { enqueueError: (payload: ErrorPayload) => void } }
  try {
    const transport = (tarslogs as unknown as TarslogsRuntime).transport
    if (!transport) return
    const enqueueError = transport.enqueueError.bind(transport)
    transport.enqueueError = (payload) => {
      const rawUrl = payload.context?.url
      if (typeof rawUrl !== 'string') {
        enqueueError(payload)
        return
      }
      let safeUrl = '[REDACTED]'
      try {
        const sanitizedUrl = new URL(rawUrl)
        sanitizedUrl.hash = ''
        safeUrl = sanitizedUrl.toString()
      } catch {
        // Keep telemetry useful without forwarding an unexpected URL value.
      }
      enqueueError({
        ...payload,
        context: { ...payload.context, url: safeUrl },
      })
    }
  } catch {
    // Prefer disabling telemetry to risking capability leakage after a client upgrade.
    return
  }

  // Vue error handler
  app.use(TarslogsVue, { client: tarslogs })

  // Global auto-capture (window.onerror, unhandledrejection)
  setupAutoCapture(tarslogs)
}

export { tarslogs }
